import type { SpinResponse } from "@slot/client-engine";
import { cannonFire, cannonLoadout } from "../cannonFixtureHelpers";
import { fx, spinFixture } from "@slot/game-shell";

const mask = (indices: number[]): string =>
  Array.from({ length: 20 }, (_, i) => (indices.includes(i) ? "1" : "0")).join("");

/**
 * Tumbling reels — the CHANCE (CH) symbol, in the CENTRE of the grid, after one normal cascade; in the backend's shape
 * (slot-backend-platform `game-math-cannon`, `evaluateSpin`): a pay-anywhere win is 6+ of a symbol, every copy wins and
 * is paid bet × paytable × the fired rung, and a CH detonates only on a dead grid, blasting its full Moore
 * neighbourhood (8 cells in the centre).
 *
 *   seq0 reveal   : H1 ×6: 60 × 2 = 120 (the cannon fires x2), removed. The CH sits at index 12 (r2c2, the centre),
 *                   in a column the win does not touch, so it stays put.
 *   seq1 collapse : the drop leaves a DEAD grid (no symbol reaches 6) with the CH at the centre →
 *                   `detonate_chance_symbol`: the CH blasts its 8 neighbours and the blast removes all nine cells (no
 *                   `remove_symbols_by_mask` follows). Columns 1-3 keep only their top symbol, which falls three rows,
 *                   and fresh symbols land on top. No money.
 *   seq2 collapse : the refill lands L5 ×6: 40 × 3 = 120 (the cannon fires x3), removed.
 *   seq3 collapse : dead, no CH → the round ends. WinMeter 0 → 120 → (120) → 240.
 *
 * Every collapse grid is the exact gravity of (previous grid − removed cells): survivors keep their symbol and fall,
 * fresh symbols fill only the vacated top cells. Checked by script (2026-10-03): the wins, the dead grids, the blast
 * and the gravity of each step.
 *
 * Masks (5×4 = 20 cells, index = row*5 + col):
 *   win1 (H1 ×6)  = 0,3,6,10,13,16
 *   CH source     = 12   targets = 6,7,8,11,13,16,17,18   blast = source ∪ targets
 *   win2 (L5 ×6)  = 1,2,7,12,17,19
 */
export function baseFixtureReelChance(): SpinResponse {
  const win1 = mask([0, 3, 6, 10, 13, 16]);              // H1 ×6 — the normal first-cascade win
  const chanceTargets = mask([6, 7, 8, 11, 13, 16, 17, 18]); // the centre CH's Moore neighbourhood
  const win2 = mask([1, 2, 7, 12, 17, 19]);              // L5 ×6 — the win the blast's refill lands

  return spinFixture("reel-chance")
    .balance("EUR", "100000")
    // --- seq 0: reveal — H1 ×6 (cannon x2), removed; the CH at 12 (centre) stays put ---
    .event("reveal", 0, (e) => {
      e.reelSet("default").betAmount("100").accumulatedWinAfter("120").gridRows([
        ["H1", "L4", "L5", "H1", "L3"],
        ["L3", "H1", "L4", "L5", "L2"],
        ["H1", "L2", "CH", "H1", "L4"],
        ["L2", "H1", "L3", "L1", "L5"],
      ]);

      const s = fx.spinBoundaryStart("base:1", "base");
      e.action(s.type, s.payload, { actionPhaseName: s.phase, debugTag: "start" });

      const load = cannonLoadout(0);
      e.action(load.type, load.payload, { actionPhaseName: load.phase, debugTag: "cannon_load" });

      const w = fx.gridWinHighlight({
        positionMask: win1, winAmount: "120", symbolIdentifier: "H1", symbolCount: "6", // 60 × 2
        lines: [{ positionMask: win1, winAmount: "120", symbolCount: "6", baseWinAmount: "60", multiplierApplied: "2" }],
      });
      e.action(w.type, w.payload, { actionPhaseName: w.phase, debugTag: "win1" });

      const fire1 = cannonFire(0);
      e.action(fire1.type, fire1.payload, { actionPhaseName: fire1.phase, debugTag: "cannon_fire1" });

      const r = fx.removeSymbolsByMask(win1);
      e.action(r.type, r.payload, { actionPhaseName: r.phase, debugTag: "remove1" });

      const t = fx.updateTotalWinMeter("120");
      e.action(t.type, t.payload, { actionPhaseName: t.phase, debugTag: "total1" });
    })
    // --- seq 1: collapse — DEAD grid; the CENTRE CH detonates its 8 neighbours (no money). Columns 1-3 keep only
    //     their top symbol, which falls three rows, and fresh symbols land on top. ---
    .event("collapse", 1, (e) => {
      e.reelSet("default").betAmount("100").accumulatedWinAfter("120").gridRows([
        ["H2", "L1", "L5", "H4", "L3"],
        ["L1", "H3", "L4", "H2", "L2"],
        ["L3", "L4", "CH", "L5", "L4"],
        ["L2", "L2", "L3", "L1", "L5"],
      ]);

      // `detonate_chance_symbol` is GAME-DEFINED (the chance feature's plugin registers it), so it is authored as a
      // literal. Phase "win" REQUIRED: the platform's resolver does not know the type, and the detonation must always run.
      const detonate = {
        type: "detonate_chance_symbol",
        phase: "win" as const,
        payload: { sourcePositionIndex: "12", targetMask: chanceTargets },
      };
      e.action(detonate.type, detonate.payload, { actionPhaseName: detonate.phase, debugTag: "chance" });
    })
    // --- seq 2: collapse — the refill lands L5 ×6 (cannon x3), removed ---
    .event("collapse", 2, (e) => {
      e.reelSet("default").betAmount("100").accumulatedWinAfter("240").gridRows([
        ["H2", "L5", "L5", "H1", "L3"],
        ["L1", "H3", "L5", "L4", "L2"],
        ["L3", "H2", "L5", "L1", "L4"],
        ["L2", "L1", "L5", "H4", "L5"],
      ]);

      const w = fx.gridWinHighlight({
        positionMask: win2, winAmount: "120", symbolIdentifier: "L5", symbolCount: "6", // 40 × 3
        lines: [{ positionMask: win2, winAmount: "120", symbolCount: "6", baseWinAmount: "40", multiplierApplied: "3" }],
      });
      e.action(w.type, w.payload, { actionPhaseName: w.phase, debugTag: "win2" });

      const fire2 = cannonFire(1);
      e.action(fire2.type, fire2.payload, { actionPhaseName: fire2.phase, debugTag: "cannon_fire2" });

      const r = fx.removeSymbolsByMask(win2);
      e.action(r.type, r.payload, { actionPhaseName: r.phase, debugTag: "remove2" });

      const t = fx.updateTotalWinMeter("240");
      e.action(t.type, t.payload, { actionPhaseName: t.phase, debugTag: "total2" });
    })
    // --- seq 3: collapse — dead, no CH: the round ends ---
    .event("collapse", 3, (e) => {
      e.reelSet("default").betAmount("100").accumulatedWinAfter("240").gridRows([
        ["H2", "L5", "H4", "H1", "H3"],
        ["L1", "H3", "L3", "L4", "L3"],
        ["L3", "H2", "L5", "L1", "L2"],
        ["L2", "L1", "L2", "H4", "L4"],
      ]);

      const end = fx.spinBoundaryEnd("base:1", "240");
      e.action(end.type, end.payload, { actionPhaseName: end.phase, debugTag: "end" });
    })
    .build();
}
